import { CostSectionConfig, FieldConfig } from './CostSection';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';

// Labor configuration (matching backend data structure)
export const laborConfig: CostSectionConfig = {
	label: 'Labor',
	fields: [
		{
			key: 'roleType',
			label: 'Role Type',
			type: 'text',
			placeholder: 'e.g. Technician, Engineer',
			required: true,
		},
		{
			key: 'numberOfTech',
			label: 'Number of Techs',
			type: 'number',
			placeholder: 'Number of technicians',
			required: true,
			min: 1,
		},
		{
			key: 'seconds',
			label: 'Time',
			type: 'time',
			required: true,
		},
		{
			key: 'hourlyRate',
			label: 'Hourly Rate',
			type: 'number',
			placeholder: 'Rate per hour',
			required: true,
			min: 0,
		},
	],
	calculateAmount: (item) => {
		const hourlyRate = Number(item.hourlyRate) || 0;
		const seconds = Number(item.seconds) || 0;
		const numberOfTech = Number(item.numberOfTech) || 0;

		if (!hourlyRate || !seconds || !numberOfTech) return 0;

		// Convert seconds to hours
		const timeInHours = seconds / 3600;

		// Calculate: hourlyRate * timeInHours * numberOfTech
		const amount = hourlyRate * timeInHours * numberOfTech;
		return Math.round(amount * 100) / 100; // Round to 2 decimal places
	},
	getDisplayText: (item, currency) => {
		const seconds = Number(item.seconds) || 0;
		const numberOfTech = Number(item.numberOfTech) || 0;
		const hourlyRate = Number(item.hourlyRate) || 0;

		// Convert seconds to appropriate time unit for display
		let timeDisplay;
		if (seconds >= 3600) {
			// Show in hours if 1 hour or more
			const hours = seconds / 3600;
			timeDisplay = `${hours.toFixed(2)} hr`;
		} else {
			// Show in minutes if less than 1 hour
			const minutes = seconds / 60;
			timeDisplay = `${minutes.toFixed(0)} min`;
		}

		return `${numberOfTech} ${item.roleType} × ${timeDisplay} @ ${currency.format(hourlyRate)}/hr`;
	},
	getDefaultItem: () => ({
		roleType: 'Technician',
		seconds: 3600, // 1 hour in seconds
		hourlyRate: 5,
		numberOfTech: 1,
		amount: 5,
	}),
};

// Parts configuration (matching backend data structure)
export const partsConfig: CostSectionConfig = {
	label: 'Parts',
	fields: [
		{
			key: 'description',
			label: 'Description',
			type: 'text',
			placeholder: 'e.g. Oil filter, Brake pads',
			required: true,
		},
		{
			key: 'partNumber',
			label: 'Part Number',
			type: 'text',
			placeholder: 'Part number or SKU',
			required: false,
		},
		{
			key: 'unitType',
			label: 'Unit Type',
			type: 'text',
			placeholder: 'e.g. each, box, set, meter, liter',
			required: true,
			validationRules: [
				{
					required: true,
					message: 'Please add unit of measure.',
				},
			],
		},
		{
			key: 'quantity',
			label: 'Quantity',
			type: 'number',
			placeholder: 'Quantity',
			required: true,
			min: 1,
			step: 1,
		},
		{
			key: 'unitPrice',
			label: 'Unit Price',
			type: 'number',
			placeholder: 'Price per unit',
			required: true,
			min: 0,
		},
	],
	calculateAmount: (item) => {
		const quantity = Number(item.quantity) || 0;
		const unitPrice = Number(item.unitPrice) || 0;
		if (!quantity || !unitPrice) return 0;
		const amount = quantity * unitPrice;
		return Math.round(amount * 100) / 100; // Round to 2 decimal places
	},
	getDisplayText: (item, currency) => {
		const quantity = Number(item.quantity) || 0;
		const unitPrice = Number(item.unitPrice) || 0;
		const partNumberText = item.partNumber ? ` (${item.partNumber})` : '';
		return `${quantity} ${item.unitType} ${item.description}${partNumberText} @ ${currency.format(
			unitPrice
		)}/${item.unitType}`;
	},
	getDefaultItem: () => ({
		description: 'Part',
		partNumber: '',
		unitType: 'each',
		quantity: 1,
		unitPrice: 0,
		amount: 0,
	}),
};

// Equipment configuration (matching backend data structure)
export const equipmentConfig: CostSectionConfig = {
	label: 'Equipment',
	fields: [
		{
			key: 'description',
			label: 'Equipment Description',
			type: 'text',
			placeholder: 'e.g. Forklift, Crane, Loader',
			required: true,
		},
		{
			key: 'equipmentNumber',
			label: 'Equipment Number',
			type: 'number',
			placeholder: 'Equipment number',
			required: false,
		},
		{
			key: 'amount',
			label: 'Amount',
			type: 'number',
			placeholder: 'Amount',
			required: true,
			min: 0,
			step: 1,
		},
	],
	calculateAmount: (item) => {
		const amount = Number(item.amount) || 0;
		return amount;
	},
	getDisplayText: (item, currency) => {
		const amount = Number(item.amount) || 0;
		const equipmentNumberText = item.equipmentNumber ? ` (${item.equipmentNumber})` : '';
		return `${item.description}${equipmentNumberText}`;
	},
	getDefaultItem: () => ({
		description: '',
		equipmentNumber: '',
		amount: 0,
	}),
};
export const travelConfig: CostSectionConfig = {
	label: 'Travel',
	fields: [
		{
			key: 'description',
			label: 'Travel Description',
			type: 'text',
			placeholder: 'e.g. Travel to site, Travel to warehouse',
			required: true,
		},
		{
			key: 'amount',
			label: 'Amount',
			type: 'number',
			placeholder: 'Amount',
			required: true,
			min: 0,
			step: 1,
		},
	],
	calculateAmount: (item) => {
		const amount = Number(item.amount) || 0;
		return amount;
	},
	getDisplayText: (item, currency) => {
		const amount = Number(item.amount) || 0;
		return `${item.description}`;
	},
	getDefaultItem: () => ({
		description: '',
		amount: 0,
	}),
};
export const freightConfig: CostSectionConfig = {
	label: 'Freight',
	fields: [
		{
			key: 'description',
			label: 'Freight Description',
			type: 'text',
			placeholder: 'e.g. Freight to site, Freight to warehouse',
			required: true,
		},
		{
			key: 'amount',
			label: 'Amount',
			type: 'number',
			placeholder: 'Amount',
			required: true,
			min: 0,
			step: 1,
		},
	],
	calculateAmount: (item) => {
		const amount = Number(item.amount) || 0;
		return amount;
	},
	getDisplayText: (item, currency) => {
		const amount = Number(item.amount) || 0;
		return `${item.description}`;
	},
	getDefaultItem: () => ({
		description: '',
		amount: 0,
	}),
};

export const miscellaneousConfig: CostSectionConfig = {
	label: 'Miscellaneous',
	fields: [
		{
			key: 'description',
			label: 'Description',
			type: 'text',
			placeholder: 'e.g. Misc 1, Misc 2',
			required: true,
		},
		{
			key: 'amount',
			label: 'Amount',
			type: 'number',
			placeholder: 'Amount',
			required: true,
			min: 0,
			step: 1,
		},
	],
	calculateAmount: (item) => {
		const amount = Number(item.amount) || 0;
		return amount;
	},
	getDisplayText: (item, currency) => {
		const amount = Number(item.amount) || 0;
		return `${item.description}`;
	},
	getDefaultItem: () => ({
		description: '',
		amount: 0,
	}),
};

// Example usage:
// <CostSection
//   label="Labor"
//   form={form}
//   fieldKey="labor"
//   currency={currency}
//   config={laborConfig}
// />
